// frontend/src/config/analytics.js
// Modern Google Analytics 4 implementation using gtag.js directly

// Configuration
const GA_MEASUREMENT_ID = import.meta.env.REACT_APP_GA_MEASUREMENT_ID || '';

// Warn in production if GA is expected but not configured, to avoid silent failures
if (!GA_MEASUREMENT_ID && import.meta.env.PROD) {
  // eslint-disable-next-line no-console
  console.warn(
    'Google Analytics is disabled: REACT_APP_GA_MEASUREMENT_ID is not set. ' +
    'Set this environment variable to enable GA tracking in production.'
  );
}

const GA_ENABLED = Boolean(GA_MEASUREMENT_ID) && import.meta.env.PROD;
const GTAG_SCRIPT_ID = 'ga-gtag';

// Check if gtag is available
const isGtagAvailable = () => {
  return GA_ENABLED && typeof window !== 'undefined' && typeof window.gtag === 'function';
};

const ensureGtag = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
};

const loadGtagScript = () => {
  if (typeof document === 'undefined') {
    return;
  }
  if (document.getElementById(GTAG_SCRIPT_ID)) {
    return;
  }
  const script = document.createElement('script');
  script.async = true;
  script.id = GTAG_SCRIPT_ID;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

// Initialize Google Analytics (load gtag.js and set up the queue)
export const initGA = () => {
  if (!GA_ENABLED) {
    return;
  }
  ensureGtag();
  loadGtagScript();
  if (isGtagAvailable()) {
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false // We'll handle page views manually
    });
  }
};

// Track page views
export const trackPageView = (path, title) => {
  if (isGtagAvailable()) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title || document.title,
    });
  }
};

// Track events
export const trackEvent = (action, category = 'User Interaction', label = '', value = 0) => {
  if (isGtagAvailable()) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track custom events with additional parameters
export const trackCustomEvent = (eventName, parameters = {}) => {
  if (isGtagAvailable()) {
    window.gtag('event', eventName, {
      event_category: parameters.category || 'Custom',
      event_label: parameters.label || '',
      value: parameters.value || 0,
      ...parameters
    });
  }
};

// Set user properties
export const setUserProperties = (properties) => {
  if (isGtagAvailable()) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      custom_map: properties
    });
  }
};

// Track user login
export const trackUserLogin = (method = 'email') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'login', {
      method: method
    });
  }
};

// Track user signup
export const trackUserSignup = (method = 'email') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'sign_up', {
      method: method
    });
  }
};

// Track purchases/credits
export const trackPurchase = (transactionId, value, currency = 'USD', items = []) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items
    });
  }
};

// Track payment initiation for credit purchases
export const trackPaymentInitiated = ({
  packageId,
  quantity,
  extraCredits,
  totalCredits,
  totalValue,
  currency = 'USD'
} = {}) => {
  const params = {
    category: 'Payments',
    package_id: packageId,
    currency
  };

  if (typeof quantity === 'number') {
    params.quantity = quantity;
  }
  if (typeof extraCredits === 'number' && extraCredits > 0) {
    params.extra_credits = extraCredits;
  }
  if (typeof totalCredits === 'number') {
    params.total_credits = totalCredits;
  }
  if (typeof totalValue === 'number') {
    params.value = totalValue;
  }

  trackCustomEvent('payment_initiated', params);
};

export const trackPaymentRedirectFallbackShown = (packageId) => {
  trackCustomEvent('payment_redirect_fallback_shown', {
    category: 'Payments',
    package_id: packageId
  });
};

export const trackPaymentRedirectFallbackClicked = (packageId) => {
  trackCustomEvent('payment_redirect_fallback_clicked', {
    category: 'Payments',
    package_id: packageId
  });
};

export const trackPaymentVerified = (paymentData = {}, sessionId = '') => {
  const transactionId = paymentData.transactionId ||
    paymentData.transaction_id ||
    paymentData.paymentId ||
    paymentData.payment_id ||
    sessionId;

  const value = typeof paymentData.amount === 'number'
    ? paymentData.amount
    : typeof paymentData.totalAmount === 'number'
    ? paymentData.totalAmount
    : typeof paymentData.total_amount === 'number'
    ? paymentData.total_amount
    : null;

  const currency = paymentData.currency || 'USD';
  const packageId = paymentData.packageId || paymentData.package_id;

  if (transactionId && typeof value === 'number') {
    const items = packageId ? [{
      item_id: packageId,
      item_name: packageId,
      quantity: 1,
      price: value
    }] : [];
    trackPurchase(transactionId, value, currency, items);
    return;
  }

  trackCustomEvent('payment_verified', {
    category: 'Payments',
    transaction_id: transactionId || sessionId
  });
};

// Export flow tracking
export const trackExportSelectAll = (dataset, columnCount) => {
  trackEvent('select_all_columns', 'Export Creation', dataset, columnCount);
};

export const trackPreviewGenerationStarted = ({ totalColumns, format, hasCountryFilter } = {}) => {
  trackCustomEvent('preview_generation_started', {
    category: 'Export Creation',
    total_columns: totalColumns,
    format,
    has_country_filter: Boolean(hasCountryFilter)
  });
};

export const trackPreviewGenerationSuccess = ({ totalColumns, format, cost, recordCount } = {}) => {
  trackCustomEvent('preview_generation_success', {
    category: 'Export Creation',
    total_columns: totalColumns,
    format,
    cost,
    record_count: recordCount
  });
};

export const trackExportGenerationSuccess = ({ totalColumns, format, cost, exportId } = {}) => {
  trackCustomEvent('export_generation_success', {
    category: 'Export Creation',
    total_columns: totalColumns,
    format,
    cost,
    export_id: exportId
  });
};

// Track export downloads
export const trackExportDownload = (format, recordCount, exportType) => {
  trackCustomEvent('export_download', {
    category: 'Export',
    label: format,
    format: format,
    record_count: recordCount,
    export_type: exportType
  });
};

// Track credit usage
export const trackCreditUsage = (creditsUsed, remainingCredits, exportType) => {
  trackCustomEvent('credit_usage', {
    category: 'Credits',
    credits_used: creditsUsed,
    remaining_credits: remainingCredits,
    export_type: exportType
  });
};

// Track search/filter usage
export const trackSearch = (searchTerm, resultCount, filterType) => {
  trackCustomEvent('search', {
    category: 'User Engagement',
    search_term: searchTerm,
    result_count: resultCount,
    filter_type: filterType
  });
};

// Track errors
export const trackError = (errorMessage, errorType = 'JavaScript Error') => {
  trackCustomEvent('exception', {
    category: 'Error',
    description: errorMessage,
    fatal: false,
    error_type: errorType
  });
};

// Enhanced conversion tracking
export const trackConversion = (conversionId, conversionLabel, value, currency = 'USD') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'conversion', {
      send_to: `${conversionId}/${conversionLabel}`,
      value: value,
      currency: currency
    });
  }
};

// Track timing events (for performance monitoring)
export const trackTiming = (name, value, category = 'Performance') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'timing_complete', {
      name: name,
      value: value,
      event_category: category
    });
  }
};

// Set user ID for cross-device tracking
export const setUserId = (userId) => {
  if (isGtagAvailable()) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId
    });
  }
};

// Authentication flow tracking
export const trackAuthTokenValidationLogin = () => {
  trackUserLogin('token_validation');
};

export const trackAuthLogout = () => {
  trackEvent('logout', 'User Authentication', 'Manual Logout');
};

export const trackGithubLoginAttempt = () => {
  trackEvent('github_login_attempt', 'User Authentication', 'GitHub OAuth');
};

export const trackMagicLinkAttempt = () => {
  trackEvent('magic_link_attempt', 'User Authentication', 'Email Magic Link');
};

export const trackMagicLinkSent = () => {
  trackEvent('magic_link_sent', 'User Authentication', 'Email Magic Link Success');
};

export const trackMagicLinkFailed = () => {
  trackEvent('magic_link_failed', 'User Authentication', 'Email Magic Link Failed');
};

export const trackMagicLinkError = () => {
  trackEvent('magic_link_error', 'User Authentication', 'Email Magic Link Error');
};

const analyticsService = {
  initGA,
  trackPageView,
  trackEvent,
  trackCustomEvent,
  setUserProperties,
  trackUserLogin,
  trackUserSignup,
  trackPurchase,
  trackPaymentInitiated,
  trackPaymentRedirectFallbackShown,
  trackPaymentRedirectFallbackClicked,
  trackPaymentVerified,
  trackExportSelectAll,
  trackPreviewGenerationStarted,
  trackPreviewGenerationSuccess,
  trackExportGenerationSuccess,
  trackExportDownload,
  trackCreditUsage,
  trackSearch,
  trackError,
  trackConversion,
  trackTiming,
  setUserId,
  trackAuthTokenValidationLogin,
  trackAuthLogout,
  trackGithubLoginAttempt,
  trackMagicLinkAttempt,
  trackMagicLinkSent,
  trackMagicLinkFailed,
  trackMagicLinkError
};

export default analyticsService;
